import { useState, useCallback, useEffect, useRef } from 'react';
import { FeedService, FeedEvent, DefaultService, ReactionsResponse, NewPlayPayload } from '../../generated-api';

interface UseFeedResult {
  events: FeedEvent[];
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  reactionsMap: Record<string, ReactionsResponse>;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useFeed(initialLimit: number = 20): UseFeedResult {
  const [events, setEvents] = useState<FeedEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [reactionsMap, setReactionsMap] = useState<Record<string, ReactionsResponse>>({});
  
  // Use ref to access current events without adding it to dependency array
  const eventsRef = useRef<FeedEvent[]>([]);
  
  // Keep ref in sync with state
  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  const fetchReactionsBatch = useCallback(async (playIds: string[]) => {
    if (playIds.length === 0) return {};
    
    try {
      // Use batch endpoint if available, otherwise fall back to individual calls
      const playIdsStr = playIds.join(',');
      const batchResponse = await DefaultService.getReactionsBatchPlaysReactionsBatchGet({
        playIds: playIdsStr,
      });
      return batchResponse as Record<string, ReactionsResponse>;
    } catch (err) {
      console.error('Failed to fetch reactions batch, falling back to individual calls:', err);
      // Fallback to individual calls if batch endpoint fails
      const reactions: Record<string, ReactionsResponse> = {};
      await Promise.all(
        playIds.map(async (playId) => {
          try {
            const response = await DefaultService.getPlayReactionsPlaysPlayIdReactionsGet({
              playId,
            });
            reactions[playId] = response;
          } catch (e) {
            // Silently fail for individual reactions
            console.error(`Failed to fetch reactions for play ${playId}:`, e);
          }
        })
      );
      return reactions;
    }
  }, []);

  const fetchFeed = useCallback(async (token?: string, append: boolean = false) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await FeedService.getActivityFeedFeedGet({
        limit: initialLimit,
        nextToken: token || undefined,
      });

      // Use ref to access current events without adding to dependency array
      const newEvents = append ? [...eventsRef.current, ...response.events] : response.events;
      setEvents(newEvents);
      setNextToken(response.nextToken || null);
      setHasMore(!!response.nextToken);

      // Extract play IDs from NEW_PLAY events and batch fetch reactions
      const playIds = newEvents
        .filter((e: FeedEvent) => e.eventType === 'NEW_PLAY')
        .map((e: FeedEvent) => (e.payload as NewPlayPayload).playId);
      
      if (playIds.length > 0) {
        const reactions = await fetchReactionsBatch(playIds);
        if (append) {
          setReactionsMap(prev => ({ ...prev, ...reactions }));
        } else {
          setReactionsMap(reactions);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load feed';
      setError(errorMessage);
      console.error('Error fetching feed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [initialLimit, fetchReactionsBatch]);

  const loadMore = useCallback(async () => {
    if (nextToken && !isLoading) {
      await fetchFeed(nextToken, true);
    }
  }, [nextToken, isLoading, fetchFeed]);

  const refresh = useCallback(async () => {
    setNextToken(null);
    await fetchFeed();
  }, [fetchFeed]);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  return {
    events,
    isLoading,
    error,
    hasMore,
    reactionsMap,
    loadMore,
    refresh,
  };
}
